import { MapsContext } from '@platform/maplibre';
import { Dropdowns } from '@platform/shared/ui';
import { ProjectTypes } from '@platform/types';
import produce from 'immer';
import React, { useState } from 'react';
import SocialExplorerWorkingLayerAutoAdjust from './SocialExplorer/SocialExplorerWorkingLayerAutoAdjust';
import { getShadedWorkingLayer } from './SocialExplorer/utils';
import WorkingLayerColorPicker from './WorkingLayerColorPicker';
import WorkingLayerOpacity from './WorkingLayerOpacity';

const { useMapsContext } = MapsContext;

interface Props {
  workingLayer: ProjectTypes.AnyWorkingLayer;
  onUpdate: (workingLayer: ProjectTypes.AnyWorkingLayer) => void;
}

const WorkingLayerStyle: React.FC<Props> = ({ workingLayer, onUpdate }) => {
  const { activeMap } = useMapsContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const renderingOptions: Dropdowns.DropdownOption[] = [{ value: 'simple', name: 'Simple' }];

  if (workingLayer.type === ProjectTypes.LayerType.POINT) {
    renderingOptions.push({ value: 'heatmap', name: 'Heatmap' });
  } else if (workingLayer.type === ProjectTypes.LayerType.POLYGON) {
    renderingOptions.push({ value: 'shaded', name: 'Choropleth' });
  }

  const handleRenderingChange = async (opt: Dropdowns.DropdownOption) => {
    try {
      setIsLoading(true);
      switch (opt.value) {
        case 'shaded':
          if (workingLayer.source === 'social-explorer') {
            const currentWorkingLayer = workingLayer as ProjectTypes.SocialExplorerWorkingLayer;
            const { id, metadata, variableSelection } = currentWorkingLayer;
            const { surveyName, datasetAbbreviation } = metadata;

            const shadedWorkingLayer = await getShadedWorkingLayer({
              id,
              surveyName,
              datasetAbbreviation,
              variableSelection,
              currentGeoLayerId: metadata.geoLayerId,
            });

            onUpdate({
              ...currentWorkingLayer,
              rendering: shadedWorkingLayer.rendering,
              metadata: shadedWorkingLayer.metadata,
            });
          }
          break;
        case 'heatmap':
          {
            const heatmapRendererStyle: ProjectTypes.HeatRenderingStyle = {
              baseColor: 'rgb(230, 103, 98)',
              opacities: [0.5, 0.5],
            };
            onUpdate({ ...workingLayer, rendering: { type: 'heatmap', style: heatmapRendererStyle } });
          }
          break;
        case 'simple':
          {
            const simpleRendererStyle: ProjectTypes.SimpleRenderingStyle = {
              baseColor: 'rgb(230, 103, 98)',
              opacities: [0.5, 0.5],
            };
            onUpdate({ ...workingLayer, rendering: { type: 'simple', style: simpleRendererStyle } });
          }
          break;
        default:
      }
    } catch {
      // TODO:
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 py-3">
      <Dropdowns.Dropdown
        disabled={isLoading}
        options={renderingOptions}
        onSelect={handleRenderingChange}
        placeholder="Applying changes"
        value={isLoading ? null : workingLayer.rendering.type}
      />
      {workingLayer.rendering.type === 'shaded' && (
        <div className="flex flex-col gap-4">
          <div className="flex w-full items-center gap-2">
            <div className="flex basis-1/5">
              <span className="text-sm">Palette</span>
            </div>
            <div className="flex basis-4/5 flex-wrap gap-1.5 py-1">
              <div className="flex w-full overflow-hidden rounded-md">
                {(workingLayer.rendering.style as ProjectTypes.ShadedRenderingStyle).cutPointSets[0].cutPoints.map(
                  (x) => (
                    <div key={x.color} style={{ background: x.color }} className="h-[22px] flex-grow" />
                  )
                )}
              </div>
            </div>
            {workingLayer.source === 'social-explorer' && (
              <SocialExplorerWorkingLayerAutoAdjust
                workingLayer={workingLayer as ProjectTypes.SocialExplorerWorkingLayer}
                map={activeMap?.ref ?? null}
                onChange={(updatedStyle: ProjectTypes.ShadedRenderingStyle) => {
                  const updated = produce(workingLayer, (draft) => {
                    draft.rendering.style = updatedStyle;
                  });
                  onUpdate(updated);
                }}
              />
            )}
          </div>
        </div>
      )}
      {workingLayer.rendering.type === 'simple' && (
        <WorkingLayerColorPicker
          workingLayerId={workingLayer.id}
          onChange={(baseColor) => {
            const updated = produce(workingLayer, (draft) => {
              (draft.rendering.style as ProjectTypes.SimpleRenderingStyle).baseColor = baseColor;
            });
            onUpdate(updated);
          }}
          value={(workingLayer.rendering.style as ProjectTypes.SimpleRenderingStyle).baseColor}
        />
      )}
      <WorkingLayerOpacity
        opacities={workingLayer.rendering.style.opacities}
        onChange={(opacities) => {
          const updated = produce(workingLayer, (draft) => {
            draft.rendering.style.opacities = opacities;
          });
          onUpdate(updated);
        }}
      />
    </div>
  );
};

export default WorkingLayerStyle;
